import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { BookOpenIcon } from "./GroupsEmptyState";
import { colors } from "utils";
import { primaryButtonStyles, PrimaryButtonCenteredLabel } from "./PrimaryButton";
import { insideStroke } from "./cardStroke";

// "See all group types" (Figma 6178:6460, Body 6178:6485): the three group
// types an empty class can start with, when warm_up_challenges is on.

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 32px 64px;
  text-align: center;
`;

const Message = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Heading = styled.h2`
  color: #000;
  font-family: "ABC Marfa", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
  text-align: center;
  width: 504px;
`;

const Cards = styled.div`
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
`;

const Card = styled.article`
  background: #ffffff;
  border: none;
  border-radius: 8px;
  box-shadow: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: space-between;
  padding: 16px;
  position: relative;
  width: 265px;
  ${insideStroke(8)}
`;

const CardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
`;

const CardTitle = styled.h3`
  color: #000;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
  margin: 0;
`;

const CardCopy = styled.p`
  color: #000;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
`;

const CardButton = styled.button`
  ${primaryButtonStyles}
  display: flex;
  justify-content: center;
  padding: 0;
  width: 100%;

  &:disabled {
    background: ${colors.gray700};
    cursor: not-allowed;
  }
`;

const GroupTypeCard = ({ title, copy, buttonLabel, onClick, disabled = false, buttonTitle }) => (
  <Card>
    <CardText>
      <CardTitle>{title}</CardTitle>
      <CardCopy>{copy}</CardCopy>
    </CardText>
    <CardButton type="button" onClick={onClick} disabled={disabled} title={buttonTitle}>
      <PrimaryButtonCenteredLabel>{buttonLabel}</PrimaryButtonCenteredLabel>
    </CardButton>
  </Card>
);

export const GroupTypesEmptyState = ({
  // Tail of the Lesson Groups sentence, per flow, as in today's empty state.
  lessonGroupsDetail = "align with your daily curriculum and with groups for core, prerequisite, and extension levels.",
  onCreateLessonGroups,
  lessonGroupsDisabled = false,
  lessonGroupsTitle,
  onCreateQuickGroup,
  onCreateWarmUpGroups,
  style,
}) => {
  const headingRef = useRef(null);

  // The link that opened this view is gone; keyboard and screen-reader users
  // continue from the heading.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <Container style={style} data-testid="group-types-empty-state">
      <Message>
        <BookOpenIcon />
        <Heading ref={headingRef} tabIndex={-1}>Let&rsquo;s set up your first small groups!</Heading>
      </Message>
      <Cards>
        <GroupTypeCard
          title="Lesson Groups"
          copy={`Create Lesson Groups to ${lessonGroupsDetail}`}
          buttonLabel="Create Lesson Groups"
          onClick={onCreateLessonGroups}
          disabled={lessonGroupsDisabled}
          buttonTitle={lessonGroupsTitle}
        />
        <GroupTypeCard
          title="Quick Group"
          copy="Create a Quick Group to instantly generate a standalone group code for targeted practice on a specific standard."
          buttonLabel="Create Quick Group"
          onClick={onCreateQuickGroup}
        />
        <GroupTypeCard
          title="Warm-Up Groups"
          copy="Warm-Up Challenges need no prior instruction. They are the perfect way to get every student thinking and talking about math!"
          buttonLabel="Create Warm-Up Groups"
          onClick={onCreateWarmUpGroups}
        />
      </Cards>
    </Container>
  );
};
